import { analytics } from '@/lib/analytics';
import { FILES_ORIGIN } from '@/utils/storage/buckets';
import {
  coloFromRay,
  isModelFileRoute,
  resourceBreakdown,
  summarizeZip,
  type FetchKind,
  type FileTiming,
} from '@/utils/models/fetch-timing';

/** The player fetches inside its package, so only the browser's timing entry sees it; sampled. */
const PLAYER_SAMPLE_RATE = 0.1;
const CLAIM_TTL_MS = 120_000;
const ENTRY_WAIT_MS = 2_000;
const RECENT_MAX = 64;

type Entry = Pick<
  PerformanceResourceTiming,
  'name' | 'startTime' | 'responseStart' | 'responseEnd' | 'encodedBodySize'
>;

export type FileTimingDeps = {
  capture: (props: Record<string, unknown> & { kind: FetchKind }) => void;
  now: () => number;
  random: () => number;
  /** Subscribes to resource entries; returns false where the browser has no PerformanceObserver. */
  observe: (onEntry: (e: Entry) => void) => boolean;
  absolute: (url: string) => string;
};

const isFileFetch = (name: string) => isModelFileRoute(name) || name.startsWith(`${FILES_ORIGIN}/`);

export function createFileTiming(deps: FileTimingDeps) {
  const claimed = new Map<string, number>();
  const recent = new Map<string, Entry>();
  const waiters = new Map<string, (e: Entry) => void>();
  let observing = false;

  const claim = (url: string) => claimed.set(url, deps.now() + CLAIM_TTL_MS);
  const isClaimed = (url: string) => (claimed.get(url) ?? 0) > deps.now();

  function onEntry(e: Entry) {
    if (!isFileFetch(e.name)) return;
    // A files. name is only known to us after the redirect, so keep those until claimed or evicted.
    if (isClaimed(e.name) || !isModelFileRoute(e.name)) {
      const waiter = waiters.get(e.name);
      if (waiter) {
        waiters.delete(e.name);
        waiter(e);
        return;
      }
      recent.set(e.name, e);
      if (recent.size > RECENT_MAX) recent.delete(recent.keys().next().value as string);
      return;
    }
    if (deps.random() >= PLAYER_SAMPLE_RATE) return;
    const b = resourceBreakdown(e);
    deps.capture({ kind: 'player', files: 1, ...b, colo: null, cache: null });
  }

  function start() {
    if (!observing) observing = deps.observe(onEntry);
  }

  function entryFor(names: string[]): Promise<Entry | null> {
    for (const n of names) {
      const e = recent.get(n);
      if (e) {
        recent.delete(n);
        return Promise.resolve(e);
      }
    }
    if (!observing) return Promise.resolve(null);
    return new Promise((resolve) => {
      const done = (e: Entry | null) => {
        names.forEach((n) => waiters.delete(n));
        resolve(e);
      };
      names.forEach((n) => waiters.set(n, done));
      setTimeout(() => done(null), ENTRY_WAIT_MS);
    });
  }

  /** Start before `fetch(url)`; `finish` after the body is read. Never throws into the caller. */
  function time(url: string) {
    start();
    const t0 = deps.now();
    const requested = deps.absolute(url);
    claim(requested);
    return {
      async finish(response: Response, bytes: number): Promise<FileTiming> {
        const duration_ms = Math.round(deps.now() - t0);
        if (response.url) claim(response.url);
        const entry = await entryFor([requested, response.url].filter(Boolean));
        return {
          duration_ms,
          ttfb_ms: entry ? resourceBreakdown(entry).ttfb_ms : null,
          bytes,
          colo: coloFromRay(response.headers.get('cf-ray')),
          cache: response.headers.get('cf-cache-status'),
        };
      },
    };
  }

  function reportDownload(timing: Promise<FileTiming>) {
    timing.then((t) => deps.capture({ kind: 'download', files: 1, ...t })).catch(() => {});
  }

  function reportZip(timings: Promise<FileTiming>[], wallMs: number) {
    Promise.all(timings)
      .then((ts) => {
        const s = summarizeZip(ts, wallMs);
        if (s) deps.capture({ kind: 'zip', ...s });
      })
      .catch(() => {});
  }

  return { start, time, reportDownload, reportZip, onEntry };
}

function browserObserve(onEntry: (e: Entry) => void): boolean {
  if (typeof PerformanceObserver === 'undefined') return false;
  try {
    new PerformanceObserver((list) =>
      list.getEntries().forEach((e) => onEntry(e as PerformanceResourceTiming))
    ).observe({
      type: 'resource',
      buffered: true,
    });
    return true;
  } catch {
    return false;
  }
}

/** Model file fetch timing for the browser, reported as `model.files.fetched`. */
export const fileTiming = createFileTiming({
  capture: (props) => analytics.capture('model.files.fetched', props),
  now: () => performance.now(),
  random: Math.random,
  observe: browserObserve,
  absolute: (url) => new URL(url, window.location.href).href,
});
